import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useChartColors } from '../../hooks/useChartColors';

interface HorizontalBarListProps<T> {
  data: T[];
  categoryKey: keyof T & string;
  valueKey: keyof T & string;
  /** Single hue for nominal categories (endpoints/handlers) — semantic, not index-based. */
  color: string;
  height?: number;
  categoryWidth?: number;
  categoryFormatter?: (value: string) => string;
}

/**
 * Generic horizontal single-hue bar list (86d3peur0 Phase C) — the "hammer" (calls by origin) and
 * posts-by-handler charts. Nominal categories share ONE hue (house dataviz rule); value labels on the ends.
 */
export function HorizontalBarList<T>({
  data,
  categoryKey,
  valueKey,
  color,
  height = 240,
  categoryWidth = 150,
  categoryFormatter,
}: HorizontalBarListProps<T>) {
  const c = useChartColors();

  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 28, top: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={c.border} horizontal={false} />
          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: c.muted }} stroke={c.border} />
          <YAxis
            type="category"
            dataKey={categoryKey}
            width={categoryWidth}
            tick={{ fontSize: 11, fill: c.muted }}
            stroke={c.border}
            tickFormatter={categoryFormatter}
          />
          <Tooltip
            cursor={{ fill: c.muted, fillOpacity: 0.08 }}
            contentStyle={{
              fontSize: 12,
              backgroundColor: c.card,
              border: `1px solid ${c.border}`,
              borderRadius: 6,
              color: c.foreground,
            }}
            labelStyle={{ color: c.foreground }}
            itemStyle={{ color: c.foreground }}
          />
          <Bar dataKey={valueKey} fill={color} radius={[0, 4, 4, 0]} maxBarSize={22}>
            <LabelList dataKey={valueKey} position="right" style={{ fontSize: 11, fill: c.muted }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
